



























































































































import { Vue, Component } from "vue-property-decorator";
import { mapActions, mapGetters } from "vuex";
import TablaVisualizacionPagos from "@/components/visualizacionPagos/Tabla.vue";
import ExportarExcelGenerico from "@/components/ExportarExcelGenerico.vue";
import ExportarPdfGenerico from "@/components/ExportarPdfGenerico.vue";
import { Prenomina } from "@/typings/store/plugins/easyFirestore/prenominas";
import { Orden } from "@/typings/store/plugins/easyFirestore/ordenes";

interface ItemPago {
  ESTADO: string;
  OC: string | number;
  NOMBRE: string;
  RUT: string;
  SOLICITANTE: string;
  FACTURA: string;
  TOTAL: string;
  PAGADA: string;
  NOMINA: string | number;
  F_A_NOMINA: string;
  OBSERVACION: string;
  COLOR: string;
}

function uniqSorted(arr: string[], compare?: (a: string, b: string) => number): string[] {
  const out = arr.filter((v, i, a) => a.indexOf(v) === i && v !== "");
  return compare ? out.sort(compare) : out.sort();
}

const routeGuard = (vm: Vue): boolean => {
  const permisos = vm.getUsuario?.permisos;
  if (permisos && permisos["visualizacionPagos"]) {
    return true;
  }

  const rutas = vm.getRutasUsuario;
  if (rutas.length > 0) {
    const to = rutas[0].to;
    if (to) {
      if (vm.$route.name !== to.name) {
        vm.$router.replace(to);
      }
      return false;
    }
  }
  if (vm.$route.name !== "inicio") {
    vm.$router.replace({ name: "inicio" });
  }
  return false;
};

@Component({
  computed: mapGetters({
    getUsuario: "usuario/get",
    getRutasUsuario: "usuario/getRutas",
    getArrayPrenominas: "prenominas/getArray",
    getArrayOrdenes: "ordenes/getArray",
  }),
  methods: mapActions({}),
  beforeRouteEnter(to, from, next) {
    next(routeGuard);
  },
  components: {
    TablaVisualizacionPagos,
    ExportarExcelGenerico,
    ExportarPdfGenerico,
  },
})
export default class VisualizacionPagos extends Vue {
  created(): void {
    if (!routeGuard(this)) {
      return;
    }
  }

  orden: Orden | null = null;
  prenomina: Prenomina | null = null;

  filtroNombre = "";
  filtroEstado = "";
  filtroOrden = "";
  filtroSolicitante = "";
  filtroNomina = "";

  buscar = "";

  readonly itemsFiltroEstado = [
    "PAGADO",
    "PARA PAGO",
    "PRENOMINA",
    "OC APROBADA - SIN FACTURA",
    "OC - CON FACTURA",
    "OC - PENDIENTE APROBACIÓN",
  ];

  /** Lista completa sin filtrar: prenóminas (con solicitante cruzado por OC) + órdenes */
  get rawItemsPagos(): ItemPago[] {
    const prenominas = this.getArrayPrenominas;
    const ordenes = this.getArrayOrdenes;
    const ordenByOC: Record<string, Orden> = {};
    ordenes.forEach((o) => {
      ordenByOC[String(o.ordenCompra)] = o;
    });
    const items: ItemPago[] = [];

    const sinSueldos = (p: Prenomina) => p.tipoGasto !== "SUELDOS";
    const sortByOC = (a: { ordenCompra: string }, b: { ordenCompra: string }) =>
      parseInt(b.ordenCompra, 10) - parseInt(a.ordenCompra, 10);

    const toItemPrenomina = (
      p: Prenomina,
      ESTADO: string,
      PAGADA: string,
      NOMINA: string | number,
      F_A_NOMINA: string,
      COLOR: string
    ): ItemPago => ({
      ESTADO,
      OC: p.ordenCompra,
      NOMBRE: p.nombreProveedor,
      RUT: p.rut,
      SOLICITANTE: ordenByOC[String(p.ordenCompra)]?.solicitante ?? "-",
      FACTURA: p.nFactura,
      TOTAL: (parseInt(p.montoAP, 10).toCurrency({ symbol: "$" }) ?? "").toString(),
      PAGADA,
      NOMINA,
      F_A_NOMINA,
      OBSERVACION: p.observacion,
      COLOR,
    });

    // Prenóminas: PAGADO
    prenominas
      .filter((p) => sinSueldos(p) && p.ingresada === true && p.estado === "aprobado")
      .sort(sortByOC)
      .forEach((p) => items.push(toItemPrenomina(p, "PAGADO", "SI", p.numeroPrenominas, p.pasoNomina, "success")));

    // Prenóminas: PARA PAGO
    prenominas
      .filter((p) => sinSueldos(p) && p.ingresada !== true && p.estado === "aprobado" && p.numeroPrenominas === 0)
      .sort(sortByOC)
      .forEach((p) => items.push(toItemPrenomina(p, "PARA PAGO", "NO", "-", "-", "warning")));

    // Prenóminas: PRENOMINA (pendientes)
    prenominas
      .filter((p) => sinSueldos(p) && p.ingresada !== true && p.estado === "pendiente")
      .sort(sortByOC)
      .forEach((p) => items.push(toItemPrenomina(p, "PRENOMINA", "NO", "-", "-", "blue")));

    const sinFactura = (o: Orden) =>
      o.tipoGasto !== "SUELDOS" &&
      o.tipoArchivo !== "BOLETA/FACTURA" &&
      o.tipoArchivo2 !== "BOLETA/FACTURA" &&
      o.tipoArchivo3 !== "BOLETA/FACTURA" &&
      o.tipoArchivo4 !== "BOLETA/FACTURA" &&
      o.tipoArchivo5 !== "BOLETA/FACTURA" &&
      o.tipoArchivo6 !== "BOLETA/FACTURA";
    const conFactura = (o: Orden) =>
      o.tipoGasto !== "SUELDOS" &&
      (o.tipoArchivo === "BOLETA/FACTURA" ||
        o.tipoArchivo2 === "BOLETA/FACTURA" ||
        o.tipoArchivo3 === "BOLETA/FACTURA" ||
        o.tipoArchivo4 === "BOLETA/FACTURA" ||
        o.tipoArchivo5 === "BOLETA/FACTURA" ||
        o.tipoArchivo6 === "BOLETA/FACTURA");

    const toItemOrden = (o: Orden, ESTADO: string, COLOR: string): ItemPago => ({
      ESTADO,
      OC: o.ordenCompra,
      NOMBRE: o.empresa,
      RUT: o.rut,
      SOLICITANTE: o.solicitante,
      FACTURA: "NO INGRESADA",
      TOTAL: (o.total.toCurrency({ symbol: "$" }) ?? "").toString(),
      PAGADA: "NO",
      NOMINA: "-",
      F_A_NOMINA: "-",
      OBSERVACION: o.observaciones,
      COLOR,
    });

    const ordenesSorted = [...ordenes].sort((a, b) => b.ordenCompra - a.ordenCompra);

    ordenesSorted.filter((o) => o.estado === "aprobado" && sinFactura(o)).forEach((o) => items.push(toItemOrden(o, "OC APROBADA - SIN FACTURA", "red")));
    ordenesSorted.filter((o) => o.estado === "pendiente" && conFactura(o)).forEach((o) => items.push(toItemOrden(o, "OC - CON FACTURA", "orange")));
    ordenesSorted.filter((o) => o.estado === "pendiente" && sinFactura(o)).forEach((o) => items.push(toItemOrden(o, "OC - PENDIENTE APROBACIÓN", "brown")));

    return items;
  }

  /** Aplica todos los filtros en AND */
  get itemsPrenomina(): ItemPago[] {
    const { buscar, filtroNombre, filtroEstado, filtroOrden, filtroNomina, filtroSolicitante } = this;
    const b = (buscar || "").toLowerCase();
    const fn = (filtroNombre || "").toUpperCase();
    const fe = (filtroEstado || "").toUpperCase();
    const fo = (filtroOrden || "").toUpperCase();
    const fno = (filtroNomina || "").toUpperCase();
    const fs = (filtroSolicitante || "").toUpperCase();

    return this.rawItemsPagos.filter((item) => {
      if (b && item.OBSERVACION.toLowerCase().indexOf(b) === -1) return false;
      if (fn && item.NOMBRE.toUpperCase().indexOf(fn) === -1) return false;
      if (fe && item.ESTADO.toString().toUpperCase().indexOf(fe) === -1) return false;
      if (fo && item.OC.toString().toUpperCase().indexOf(fo) === -1) return false;
      if (fno && item.NOMINA.toString().toUpperCase().indexOf(fno) === -1) return false;
      if (fs && item.SOLICITANTE.toString().toUpperCase().indexOf(fs) === -1) return false;
      return true;
    });
  }

  get itemsFiltroNombres(): string[] {
    return uniqSorted(this.rawItemsPagos.map((i) => i.NOMBRE));
  }

  get itemsFiltroOrden(): string[] {
    return uniqSorted(this.rawItemsPagos.map((i) => i.OC.toString()), (a, b) => parseInt(b, 10) - parseInt(a, 10));
  }

  get itemsFiltroNomina(): string[] {
    const set = new Set<string>();
    this.getArrayPrenominas.forEach((p) => set.add(String(p.numeroPrenominas)));
    return [...set].filter((v) => v !== "").sort((a, b) => parseInt(b, 10) - parseInt(a, 10));
  }

  get itemsFiltroSolicitante(): string[] {
    return uniqSorted(this.rawItemsPagos.map((i) => i.SOLICITANTE));
  }

  /** Columnas para exportación Excel/PDF (respeta filtros: se exporta itemsPrenomina) */
  get columnasExportacionPagos(): Array<{ label: string; field: string }> {
    return [
      { label: "Estado", field: "ESTADO" },
      { label: "N° Orden", field: "OC" },
      { label: "Proveedor", field: "NOMBRE" },
      { label: "RUT", field: "RUT" },
      { label: "Solicitante", field: "SOLICITANTE" },
      { label: "N° Factura", field: "FACTURA" },
      { label: "Costo total", field: "TOTAL" },
      { label: "Pagada", field: "PAGADA" },
      { label: "Nómina", field: "NOMINA" },
      { label: "Fecha nómina", field: "F_A_NOMINA" },
      { label: "Glosa", field: "OBSERVACION" },
    ];
  }

  /** Subtítulo del PDF cuando hay filtros activos */
  get subtituloExportacion(): string {
    const parts: string[] = [];
    if (this.filtroNombre) parts.push(`Proveedor: ${this.filtroNombre}`);
    if (this.filtroEstado) parts.push(`Estado: ${this.filtroEstado}`);
    if (this.filtroOrden) parts.push(`Orden: ${this.filtroOrden}`);
    if (this.filtroNomina) parts.push(`Nómina: ${this.filtroNomina}`);
    if (this.filtroSolicitante) parts.push(`Solicitante: ${this.filtroSolicitante}`);
    if (this.buscar) parts.push(`Buscar: ${this.buscar}`);
    return parts.length > 0 ? parts.join(" · ") : "";
  }
}
